import React, { useEffect, useState } from "react";
import "./Profile.css";
import TransLoader from "../../loader/TransLoader";
import CustomAlert from "../../custom_alert/CustomAlert";

export default function Profile() {
  const [config, setConfig] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alertMessage, setAlertMessage] = useState("");
  const [profileCompletion, setProfileCompletion] = useState(0);
  const [updating, setUpdating] = useState(false);

  const empCode = sessionStorage.getItem("user_id");

  // Load config
  useEffect(() => {
    fetch("/config.json")
      .then(res => res.json())
      .then(data => setConfig(data))
      .catch(console.error);
  }, []);

  // Fetch profile data
  useEffect(() => {
    if (!config || !empCode) return;
    const fetchProfile = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${config.BASE_URL}${config.EMPLOYEE_DATA}`, {
          method: "POST",
          credentials: "include",
          headers: { "X-Requested-With": "RC_EMP_NEW" },
          body: JSON.stringify({ emp_code: empCode }),
        });
        const data = await res.json();
        if (data.status === "success") {
          setProfile(data.employee);
          computeCompletion(data.employee);
        } else {
          setAlertMessage(data.message || "Failed to fetch profile.");
        }
      } catch (err) {
        console.error(err);
        setAlertMessage("Network error while fetching profile.");
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [config, empCode]);

  // Compute profile completion percentage
  const computeCompletion = (employee) => {
    const keys = [
      "first_name",
      "last_name",
      "dept",
      "mobile_number",
      "personal_email",
      "date_of_birth",
      "father_name",
      "mother_name",
      "aadhar_number",
      "pan_number",
      "profile_image",
      "aadhar_front",
      "aadhar_back",
      "pan_image",
    ];
    const filled = keys.reduce((acc, key) => (employee[key] ? acc + 1 : acc), 0);
    setProfileCompletion(Math.round((filled / keys.length) * 100));
  };

  // Handle form field changes
  const MAX_FILE_SIZE = 5 * 1024 * 1024;

  const handleChange = (e) => {
    const { name, files } = e.target;
    if (files && files[0]) {
      if (files[0].size > MAX_FILE_SIZE) {
        alert(`File is too large. Max allowed size is 5MB.`);
        return;
      }
      setProfile(prev => ({ ...prev, [name]: files[0] }));
    } else {
      setProfile(prev => ({ ...prev, [name]: e.target.value }));
    }
  };


  // Handle profile update
  const handleUpdate = async () => {
    if (!config || !profile) return;
    setUpdating(true);
    setAlertMessage("");
    const formData = new FormData();
    for (const key in profile) formData.append(key, profile[key]);
    if (profileCompletion === 100) {
    formData.set("profile_completed", 1);
  }else{
    formData.set("profile_completed", 0);
  }
    try {
      const res = await fetch(`${config.BASE_URL}${config.UPDATE_EMPLOYEE_DATA}`, {
        method: "POST",
        credentials: "include",
        headers: { "X-Requested-With": "RC_EMP_NEW" },
        body: formData,
      });
      const data = await res.json();
      if (data.status === "success") {
        setAlertMessage("Profile updated successfully!");
        computeCompletion(profile);
      } else {
        setAlertMessage("Update failed: " + data.message);
      }
    } catch (err) {
      console.error(err);
      setAlertMessage("Update failed due to network error.");
    } finally {
      setUpdating(false);
    }
  };

  if (loading) return <TransLoader />;
  if (!profile) return <p>No profile data available.</p>;

  return (
    <div className="profile-card">
      {/* Profile Image & Welcome */}
      <div className="profile-header">
        {profile.profile_image ? (
          <img
            src={
              typeof profile.profile_image === "string"
                ? `data:image/jpeg;base64,${profile.profile_image}`
                : URL.createObjectURL(profile.profile_image)
            }
            alt="Profile"
            className="profile-img-main"
          />
        ) : (
          <div className="no-image">No Image</div>
        )}
        <h2>WELCOME: {profile.first_name}</h2>
        <input type="file" name="profile_image" onChange={handleChange} />
      </div>

      {/* Progress Bar */}
      <div className="progress-bar-container">
        <div className="progress-bar-fill" style={{ width: `${profileCompletion}%` }}></div>
      </div>
      <div className="profile-completion-text">{profileCompletion}%</div>

      {/* Profile Fields */}
      <div className="profile-grid">
        <div className="row">
          <label>Employee ID:</label>
          <input type="text" value={profile.emp_code || ""} disabled />
        </div>

        <div className="row">

          <label>First Name:</label>
          <input type="text" name="first_name" value={profile.first_name || ""} onChange={handleChange} />
          <label>Last Name:</label>
          <input type="text" name="last_name" value={profile.last_name || ""} onChange={handleChange} />
        </div>

        <div className="row">
          <label>Role:</label>
          <input type="text" value={profile.role || ""} disabled />
          <label>Department:</label>
          <input type="text" name="dept" value={profile.dept || ""} disabled />
        </div>

        <div className="row">
          <label>Mobile Number:</label>
          <input type="text" name="mobile_number" value={profile.mobile_number || ""} onChange={handleChange} />
          <label>Personal Email:</label>
          <input type="text" name="personal_email" value={profile.personal_email || ""} onChange={handleChange} />
          <label>Date of Birth:</label>
          <input type="date" name="date_of_birth" value={profile.date_of_birth || ""} onChange={handleChange} />
        </div>

        <div className="row">
          <label>Father Name:</label>
          <input type="text" name="father_name" value={profile.father_name || ""} onChange={handleChange} />
          <label>Mother Name:</label>
          <input type="text" name="mother_name" value={profile.mother_name || ""} onChange={handleChange} />
        </div>

        <div className="row">
          <label>Aadhar Number:</label>
          <input type="text" name="aadhar_number" value={profile.aadhar_number || ""} onChange={handleChange} />
          <label>PAN Number:</label>
          <input type="text" name="pan_number" value={profile.pan_number || ""} onChange={handleChange} />
        </div>
      </div>

      {/* Images: Aadhar front/back, PAN */}
      <div className="image-group">
        {["aadhar_front", "aadhar_back", "pan_image"].map((field) => (
          <div key={field} className="image-field">
            <label>{field.replace("_", " ").toUpperCase()}</label>
            {profile[field] ? (
              <img
                src={
                  typeof profile[field] === "string"
                    ? `data:image/jpeg;base64,${profile[field]}`
                    : URL.createObjectURL(profile[field])
                }
                alt={field}
                className="profile-img-preview"
              />
            ) : (
              <div className="no-image">No Image</div>
            )}
            <input type="file" name={field} onChange={handleChange} />
          </div>
        ))}
      </div>

      {/* Update Button */}
      <div className="update-btn-container">
        <button className="update-btn" onClick={handleUpdate} disabled={updating}>
          {updating ? "Updating..." : "Update Profile"}
        </button>
      </div>

      <CustomAlert message={alertMessage} onClose={() => setAlertMessage("")} />
      {loading && <TransLoader />}
    </div>
  );
}
